import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import AuthService from '../../services/AuthService';
import BuilderService from '../../services/BuilderService';
import {
  emptyBuilderForm,
  BuilderFormFields,
  section,
  toBuilderDto,
} from './builderFormShared';

export default function EditBuilder() {
  const { id } = useParams();
  const toast = useRef(null);
  const navigate = useNavigate();
  const clientId = AuthService.getLoggedInClientId();
  const [form, setForm] = useState(emptyBuilderForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const set = (name, val) => setForm((f) => ({ ...f, [name]: val }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id || !clientId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const b = await BuilderService.getById(Number(id));
        if (cancelled || !b) {
          if (!cancelled && !b) {
            toast.current?.show({ severity: 'warn', summary: 'Not found', detail: 'Builder could not be loaded.' });
            navigate('/builders');
          }
          return;
        }
        setForm({
          companyName: b.companyName || '',
          legalName: b.legalName || '',
          email: b.email || '',
          phone: b.phone || '',
          address: b.address || '',
          city: b.city || '',
          state: b.state || '',
          defaultCommissionPercent: b.defaultCommissionPercent != null ? String(b.defaultCommissionPercent) : '',
          commissionAgreementNotes: b.commissionAgreementNotes || '',
          contactsJson: b.contactsJson || '',
          documentsJson: b.documentsJson || '',
        });
      } catch {
        if (!cancelled) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load builder.' });
          navigate('/builders');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, clientId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.companyName?.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Company name is required' });
      return;
    }
    setSaving(true);
    try {
      await BuilderService.update(toBuilderDto(clientId, form, Number(id)));
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Builder updated.', life: 2000 });
      setTimeout(() => navigate('/builders'), 1200);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Could not update builder.',
      });
    }
    setSaving(false);
  };

  if (!clientId) {
    return (
      <div style={{ padding: 24 }}>
        <p className="text-muted">Sign in with a tenant account.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 80, textAlign: 'center', background: '#f4f7fa', minHeight: '100vh' }}>
        <i className="fas fa-spinner fa-spin" style={{ fontSize: 32, color: '#5e72e4' }} />
        <p style={{ color: '#8898aa', marginTop: 12 }}>Loading builder…</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: '#f4f7fa', minHeight: '100vh' }}>
      <Toast ref={toast} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            type="button"
            onClick={() => navigate('/builders')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#5e72e4',
              fontWeight: 700,
              fontSize: 14,
              padding: 0,
            }}
          >
            <i className="fas fa-arrow-left" style={{ marginRight: 8 }} />
            Back
          </button>
          <div>
            <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1f36' }}>
              <i className="fas fa-edit" style={{ color: '#5e72e4', marginRight: 10 }} />
              Edit Builder
            </h3>
            <p style={{ margin: '6px 0 0', color: '#8898aa', fontSize: 14 }}>
              #{id} · {form.companyName || '—'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={section('#825ee4')}>
          <h6 style={{ margin: '0 0 18px', color: '#825ee4', fontWeight: 700 }}>
            <i className="fas fa-building" style={{ marginRight: 8 }} />
            Company details
          </h6>
          <BuilderFormFields form={form} set={set} />
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 28px',
              background: saving ? '#adb5bd' : 'linear-gradient(135deg,#5e72e4,#825ee4)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: 14,
            }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/builders')}
            style={{
              padding: '12px 28px',
              background: '#fff',
              color: '#525f7f',
              border: '1px solid #e3e6ec',
              borderRadius: 12,
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
